const SCROLLABLE_OVERFLOW = /auto|scroll/;

function canScroll(el: Element, deltaX: number, deltaY: number): boolean {
  const style = getComputedStyle(el);
  // The document scrolls through the viewport, whatever its own overflow says.
  const isRoot = el === document.scrollingElement;
  // `- 1`: scrollTop/scrollLeft can be fractional on high-DPI screens, so a
  // container at its far edge can sit a fraction of a pixel short of it.
  if (deltaY !== 0 && (isRoot || SCROLLABLE_OVERFLOW.test(style.overflowY))) {
    const canMove =
      deltaY < 0
        ? el.scrollTop > 0
        : el.scrollTop + el.clientHeight < el.scrollHeight - 1;
    if (canMove) return true;
  }
  if (deltaX !== 0 && (isRoot || SCROLLABLE_OVERFLOW.test(style.overflowX))) {
    const canMove =
      deltaX < 0
        ? el.scrollLeft > 0
        : el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    if (canMove) return true;
  }
  return false;
}

/**
 * True when neither the wheel target nor any of its ancestors can scroll any
 * further in the gesture's direction: the gesture can then only make the page
 * (or a container at its edge) rubber-band.
 */
export function shouldBlockWheel(
  target: EventTarget | null,
  deltaX: number,
  deltaY: number,
): boolean {
  for (
    let el = target instanceof Element ? target : null;
    el;
    el = el.parentElement
  ) {
    if (canScroll(el, deltaX, deltaY)) return false;
  }
  return true;
}

/**
 * Stand-in for `overscroll-behavior: none` on browsers that lack it (Safari
 * before 16), where a trackpad scroll that nothing can follow rubber-bands
 * the whole screen, header included. Installs nothing where the property is
 * supported. Returns the uninstaller.
 */
export function installOverscrollNoneFallback(): () => void {
  if (CSS.supports("overscroll-behavior", "none")) return () => {};

  const onWheel = (event: WheelEvent) => {
    // ctrl + wheel is a zoom (trackpad pinch), not a scroll.
    if (event.ctrlKey) return;
    if (shouldBlockWheel(event.target, event.deltaX, event.deltaY)) {
      event.preventDefault();
    }
  };
  window.addEventListener("wheel", onWheel, { passive: false });
  return () => window.removeEventListener("wheel", onWheel);
}
